/** Load content only when a grid thumbnail enters the viewport; list rows need none. */
import { useEffect, useRef, useState } from 'react';
import type { DocumentPreview, DocumentSummary } from '@shared/api';
import { documents } from '../client';
import { Thumb } from './Thumb';

export function DocumentThumb({ doc }: { doc: DocumentSummary }) {
  const frame = useRef<HTMLDivElement>(null);
  const [preview, setPreview] = useState<DocumentPreview | null>(null);
  useEffect(() => {
    setPreview(null);
    if (doc.locked || !frame.current) return;
    let live = true;
    let asked = false;
    const load = () => {
      if (asked) return;
      asked = true;
      void documents.preview(doc.id).then(p => { if (live) setPreview(p); }).catch(() => undefined);
    };
    const observer = new IntersectionObserver(entries => {
      if (entries.some(e => e.isIntersecting)) { observer.disconnect(); load(); }
    });
    observer.observe(frame.current);
    return () => { live = false; observer.disconnect(); };
  }, [doc.id, doc.updatedAt, doc.locked]);

  return <div ref={frame} className="mv-document-thumb" aria-hidden="true">
    {!preview ? <Thumb kind={doc.kind} variant="recent" locked={doc.locked} /> : preview.kind === 'docs' ? (
      <div className="mv-preview-paper">{preview.blocks.map((b, i) => b.heading ? <h4 key={i}>{b.text}</h4> : <p key={i}>{b.text}</p>)}</div>
    ) : preview.kind === 'sheets' ? (
      <div className="mv-preview-sheet"><div>{preview.name}</div><table><tbody>{preview.rows.map((row, i) => <tr key={i}>{row.map((value, j) => <td key={j}>{value}</td>)}</tr>)}</tbody></table></div>
    ) : (
      <svg className="mv-preview-slide" viewBox={`0 0 ${preview.w} ${preview.h}`}>
        <rect width={preview.w} height={preview.h} fill={preview.bg} />
        {preview.marks.map((m, i) => m.k === 'ellipse' ? <ellipse key={i} cx={m.x + m.w / 2} cy={m.y + m.h / 2} rx={m.w / 2} ry={m.h / 2} fill={m.fill} /> : <rect key={i} x={m.x} y={m.y} width={m.w} height={m.h} rx={m.r} fill={m.fill} />)}
      </svg>
    )}
  </div>;
}
